import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Spacer,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import { authFetch } from "../../utils/authFetch";

export default function UserManager() {
  const toast = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const isSystemAdmin = currentUser.role === "system-admin";

  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "staff",
    customerId: "",
  });

  const API = process.env.REACT_APP_API_URL || "";

  const fetchUsers = async () => {
    const res = await authFetch(`${API}/api/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setUsers(Array.isArray(data) ? data : []);
  };

  const fetchCustomers = async () => {
    if (!isSystemAdmin) return;
    const res = await authFetch(`${API}/api/customers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    setCustomers(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []);

  const resetForm = () => {
    setForm({
      username: "",
      password: "",
      role: "staff",
      customerId: "",
    });
    setEditingId(null);
    setFormVisible(false);
  };

  const validateForm = () => {
    if (!form.username || (!editingId && !form.password)) {
      toast({
        title: "Please enter username and password.",
        status: "warning",
      });
      return false;
    }
    if (form.role !== "system-admin" && !form.customerId) {
      toast({ title: "Please select a customer.", status: "warning" });
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;
    try {
      const res = await fetch(`${API}/api/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Add failed");
      toast({ title: "User added", status: "success" });
      resetForm();
      fetchUsers();
    } catch {
      toast({ title: "Failed to add user", status: "error" });
    }
  };

  const handleUpdate = async () => {
    // ไม่ต้อง validate form เพราะอาจไม่ใส่ password
    // if (!validateForm()) return;
    try {
      // สร้าง object ที่จะส่งไป API
      const userData = { ...form };

      // ถ้า password เป็นค่าว่าง (empty string) ให้ลบออกจาก object
      // เพื่อไม่ให้ API อัปเดตเป็นรหัสผ่านว่าง
      if (!userData.password) {
        delete userData.password;
      }

      const res = await fetch(`${API}/api/users/${editingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData), // ส่ง userData ที่อาจจะมี password หรือไม่มีก็ได้
      });
      if (!res.ok) throw new Error("Update failed");
      toast({ title: "User updated", status: "success" });
      resetForm();
      fetchUsers();
    } catch {
      toast({ title: "Update failed", status: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this user?")) return;
    try {
      await fetch(`${API}/api/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "User deleted", status: "info" });
      fetchUsers();
      resetForm();
    } catch {
      toast({ title: "Delete failed", status: "error" });
    }
  };

  return (
    <Flex justify="center" mt={10}>
      <Box p={10} w="70%" borderWidth={1} boxShadow={"lg"}>
        <Heading mb={4}>Manage Users</Heading>

        {!formVisible && (
          <Button
            colorScheme="iBlue"
            mb={6}
            onClick={() => {
              setFormVisible(true);
              setForm({
                username: "",
                password: "",
                role: "staff",
                customerId: "",
              });
              setEditingId(null);
            }}
          >
            Add User
          </Button>
        )}

        {formVisible && (
          <Stack spacing={3} mb={6}>
            <FormControl isRequired>
              <FormLabel>Username</FormLabel>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </FormControl>
            {!editingId && (
              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </FormControl>
            )}
            {/* เพิ่มฟิลด์ New Password เมื่อเป็นการ Edit User */}
            {editingId && (
              <FormControl>
                <FormLabel>
                  New Password (leave blank to keep current password)
                </FormLabel>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </FormControl>
            )}
            <FormControl isRequired>
              <FormLabel>Role</FormLabel>
              <Select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="system-admin">System Admin</option>
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
              </Select>
            </FormControl>
            {form.role !== "system-admin" && (
              <FormControl isRequired>
                <FormLabel>Customer</FormLabel>
                <Select
                  value={form.customerId}
                  onChange={(e) =>
                    setForm({ ...form, customerId: e.target.value })
                  }
                >
                  <option value="">Select customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            <Flex gap={4}>
              <Button
                colorScheme="iTeal"
                onClick={() => (editingId ? handleUpdate() : handleAdd())}
              >
                Save
              </Button>
              <Button colorScheme="iRed" onClick={resetForm}>
                Cancel
              </Button>
              {editingId && (
                <Button
                  colorScheme="iOrange"
                  onClick={() => handleDelete(editingId)}
                >
                  Delete
                </Button>
              )}
            </Flex>
          </Stack>
        )}

        <Heading size="md" mb={4}>
          User List
        </Heading>
        <Stack spacing={3}>
          {users.map((u) => {
            // --- ส่วนที่เพิ่มเข้ามา ---
            // ค้นหาชื่อ customer จาก state 'customers' โดยใช้ customerId ของ user
            const customerName = customers.find(
              (c) => c.id === u.customerId
            )?.name;

            return (
              <Box key={u.id} borderWidth={1} borderRadius="md" p={4}>
                <Flex>
                  <Box>
                    <Text fontWeight="bold">{u.username}</Text>
                    <Text fontSize="sm">Role: {u.role}</Text>

                    {/* --- ส่วนที่แก้ไข --- */}
                    {/* แสดงชื่อ Customer ที่หาได้ หรือแสดง "-" ถ้าไม่พบ */}
                    <Text fontSize="sm" color="gray.600">
                      Customer: {customerName || "-"}
                    </Text>
                  </Box>
                  <Spacer />
                  <Button
                    size="sm"
                    colorScheme="iBlue"
                    onClick={() => {
                      setFormVisible(true);
                      setEditingId(u.id);
                      setForm({
                        username: u.username,
                        password: "",
                        role: u.role,
                        customerId: u.customerId || "",
                      });
                    }}
                  >
                    Edit
                  </Button>
                </Flex>
              </Box>
            );
          })}
        </Stack>

        <Flex justify="center" mt={8}>
          <Button
            onClick={() => navigate("/")}
            colorScheme="gray"
            variant="outline"
          >
            Back to Menu
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}
